import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from './Layout';

const BilledOutTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today

  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:5001';

  useEffect(() => {
    fetchCarts();
  }, [selectedDate]); // Fetch carts when the selected date changes

  const fetchCarts = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${apiUrl}/api/carts/`);
      const allCarts = response.data;

      // Get the start and end of the selected date
      const startOfSelectedDate = new Date(selectedDate);
      startOfSelectedDate.setHours(0, 0, 0, 0);
      const endOfSelectedDate = new Date(selectedDate);
      endOfSelectedDate.setHours(23, 59, 59, 999);

      // Filter carts that are billed out and created on the selected date
      const billedOutTransactions = allCarts.filter(cart =>
        cart.billedOut &&
        new Date(cart.createdAt) >= startOfSelectedDate &&
        new Date(cart.createdAt) <= endOfSelectedDate
      );

      setTransactions(billedOutTransactions);
    } catch (error) {
      console.error('Error fetching carts:', error);
      setError('Failed to load transactions. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (event) => {
    setSelectedDate(event.target.value);
  };

  const handleReprint = async (transactionId) => {
    try {
      await axios.post(`${apiUrl}/api/transactions/reprint/${transactionId}`);
      alert('Receipt reprinted successfully');
    } catch (error) {
      console.error('Error reprinting receipt:', error);
      alert('Error reprinting receipt. Please try again.');
    }
  };

  const renderTransactionItems = (items) => (
    <ul className="list-disc pl-5">
      {items.map((item) => (
        <li key={item.productId._id} className="my-1">
          {item.productId.name} - {item.quantity} x {item.price}
          {item.notes && item.notes.length > 0 && (
            <span className="text-gray-500"> - Notes: {item.notes}</span>
          )}
        </li>
      ))}
    </ul>
  );

  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4">
        <h1 className="text-2xl font-bold mb-4">Billed-Out Transactions</h1>

        <div className="mb-4">
          <label htmlFor="date" className="block text-gray-700 font-medium">Select Date:</label>
          <input 
            type="date" 
            id="date" 
            value={selectedDate} 
            onChange={handleDateChange} 
            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm focus:border-blue-500 focus:ring focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <p className="text-gray-500">Loading transactions...</p>
        ) : error ? (
          <p className="text-red-500">{error}</p>
        ) : transactions.length === 0 ? (
          <p className="text-gray-500">No transactions found for the selected date.</p>
        ) : (
          transactions.map((transaction) => (
            <div key={transaction._id} className="bg-white shadow-md rounded-lg p-4 mb-4">
              <h2 className="text-xl font-semibold">Table: {transaction.tableNumber}</h2>
              <p className="text-gray-700">Waiter: {transaction.waiter}</p>
              <p className="text-gray-700">Transaction Type: {transaction.transactionType}</p>
              <p className="text-gray-600">Date: {new Date(transaction.createdAt).toLocaleString()}</p>

              <h3 className="text-lg font-medium mt-3">Items:</h3>
              {renderTransactionItems(transaction.items)}

              <p className="mt-2"><strong>Total Amount:</strong> {transaction.totalAmount || 'N/A'}</p>
              {transaction.VAT && <p><strong>VAT:</strong> {transaction.VAT}</p>}
              {transaction.promoDiscount && <p><strong>Discount:</strong> {transaction.promoDiscount}</p>}

              <button 
                onClick={() => handleReprint(transaction._id)} 
                className="mt-4 bg-teal-500 text-white px-4 py-2 rounded hover:bg-teal-600 transition"
              >
                Reprint Receipt
              </button>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
};

export default BilledOutTransactions;
